import { useTranslation } from "react-i18next";
import { Flex } from "rebass/styled-components";
import Lottie from "lottie-react";
import styled from "styled-components";
import {
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  FormState,
} from "react-hook-form";

import Loading from "animations/loading.json";
import { SettingsContainer } from "components/molecules/settingsContainer";
import { analytics } from "MVVM/Analytics";
import { Avatar } from "MVVM/Components/Avatar";
import { Button } from "MVVM/Components/Button";
import { CheckboxWithLabel } from "MVVM/Components/Checkbox";
import { DividerAlt } from "MVVM/Components/Divider";
import { SelectWithLabel, Error } from "MVVM/Components/Inputs";
import { ThirdPartySettingsViewModel } from "MVVM/Views/ProfileSettings/ViewModel";
import {
  FormData,
  Entry,
} from "MVVM/Views/ProfileSettings/ThirdPartySettings/types";
import { IAllstarUser } from "MVVM/@types/user";
import { Body } from "MVVM/Components/Text";
import theme from "theme/themeThree";

const lottieStyle = {
  height: 28,
  width: 28,
};

const StyledForm = styled.form`
  margin: 1rem 0 0;
`;

interface ThirdPartySettingsProps {
  handleSubmit: UseFormHandleSubmit<FormData>;
  register: UseFormRegister<FormData>;
  formState: FormState<FormData>;
  reset: UseFormReset<FormData>;
  allstarUser: IAllstarUser;
}

const getPartnerIdsByClipping = (formData: FormData, enabled: boolean) =>
  Object.entries(formData)
    .filter(([_, entry]) => entry.clippingEnabled === enabled)
    .map(([partnerId]) => partnerId);

const ThirdPartySettings = ({
  handleSubmit,
  register,
  formState,
  reset,
  allstarUser,
}: ThirdPartySettingsProps) => {
  const { data, loading, error, updateUserPartnerSettings, updateError } =
    ThirdPartySettingsViewModel();

  const { t } = useTranslation("translation", {
    keyPrefix: "account.thirdPartySettings",
  });

  if (loading) {
    return (
      <SettingsContainer
        title={t("title", "3rd Party Settings")}
        mb={6}
        subtitle={t(
          "subtitle",
          "Manage your settings for third-party integrations associated with your account.",
        )}
        subtitleTextColor="chalk"
      >
        <DividerAlt></DividerAlt>
        <Flex justifyContent={"center"}>
          <Lottie animationData={Loading} style={lottieStyle} loop={true} />
        </Flex>
      </SettingsContainer>
    );
  }

  if (error) {
    return (
      <SettingsContainer
        title={t("title", "3rd Party Settings")}
        mb={6}
        subtitle={t(
          "subtitle",
          "Manage your settings for third-party integrations associated with your account.",
        )}
        subtitleTextColor="chalk"
      >
        <DividerAlt></DividerAlt>
        <Flex justifyContent={"center"}>
          <Error>There was an error fetching your 3rd Party Settings.</Error>
        </Flex>
      </SettingsContainer>
    );
  }

  if (updateError) {
    return (
      <SettingsContainer
        title={t("title", "3rd Party Settings")}
        mb={6}
        subtitle={t(
          "subtitle",
          "Manage your settings for third-party integrations associated with your account.",
        )}
        subtitleTextColor="chalk"
      >
        <DividerAlt></DividerAlt>
        <Flex justifyContent={"center"}>
          <Error>There was an error updating your 3rd Party Settings.</Error>
        </Flex>
      </SettingsContainer>
    );
  }

  const onSubmit = async (formData: FormData) => {
    const input = Object.entries(formData).map(
      ([partnerId, entry]: [string, Entry]) => ({
        partnerId,
        clippingEnabled: entry.clippingEnabled,
        usernameType: entry.usernameType,
        studioLoadoutType: entry.studioLoadoutType,
      }),
    );

    const clippingEnabledPartnerIds = getPartnerIdsByClipping(formData, true);
    const clippingDisabledPartnerIds = getPartnerIdsByClipping(formData, false);

    const response = await updateUserPartnerSettings({ variables: { input } });

    if (response.data.updateUserPartnerSettings.success) {
      reset(formData);
    }

    analytics.identify(allstarUser?.user._id, {
      thirdPartyPartnerEnabled: clippingEnabledPartnerIds,
      thirdPartyPartnerDisabled: clippingDisabledPartnerIds,
    });
  };

  const labelStyles = {
    color: "chalk",
    fontWeight: 500,
    fontSize: "1rem",
    marginRight: "10px",
  };
  const renderIntegrations = () => {
    if (
      data.userPartnerSettings.success &&
      data.userPartnerSettings.entries.length > 0
    ) {
      return data.userPartnerSettings.entries.map((integration, index) => {
        const hasPartnerDescription = !!integration.partnerDescription;
        const lastIntegration =
          data.userPartnerSettings.entries.length === index + 1;
        const usernameTypeOptions = [
          { label: "Allstar Username", value: "ALLSTAR" },
          {
            label: `${integration.partnerName} Provided Username`,
            value: "PARTNER",
          },
          { label: "In Game Provided Username", value: "INGAME" },
        ];
        const studioLoadoutTypeOptions = [
          { label: "Allstar Settings", value: "ALLSTAR" },
          { label: `${integration.partnerName} Settings`, value: "PARTNER" },
        ];
        return (
          <Flex flexDirection="column" key={integration.partnerName}>
            <Flex alignItems="center" mb={6}>
              <Avatar
                size="large"
                image={integration.partnerLogo}
                mb={0}
                ml={0}
                mr="1.5rem"
                mt={0}
              ></Avatar>
              <Flex flexDirection="column">
                <Body color="chalk" fontSize="1.125rem" fontWeight="bold">
                  {integration.partnerName}
                </Body>
                {hasPartnerDescription && (
                  <Body
                    color="chalk"
                    fontWeight={500}
                    fontSize="1rem"
                    fontStyle="italic"
                    mt={2}
                  >
                    {integration.partnerDescription}
                  </Body>
                )}
              </Flex>
            </Flex>
            <CheckboxWithLabel
              applySpace={true}
              checkbox={{
                id: `${integration.partnerId}.clippingEnabled`,
                ...register(`${integration.partnerId}.clippingEnabled`, {
                  required: false,
                }),
                name: `${integration.partnerId}.clippingEnabled`,
                defaultChecked: integration.clippingEnabled,
              }}
              labelStyles={labelStyles}
              onClick={(e: any) => {
                analytics.track(
                  "Third Party Settings Update - Partner Change",
                  {
                    partnerId: integration.partnerId,
                    partnerName: integration.partnerName,
                    originalValue: integration.clippingEnabled,
                    updatedValue: e.target.checked,
                  },
                );
              }}
            >
              {t("clippingEnabledLabel", {
                partnerName: integration.partnerName,
                defaultValue: `Allow ${integration.partnerName} to request clips on my behalf`,
              })}
            </CheckboxWithLabel>
            <SelectWithLabel
              applySpace={true}
              input={{
                id: `${integration.partnerId}.usernameType`,
                options: usernameTypeOptions,
                ...register(`${integration.partnerId}.usernameType`, {}),
                defaultValue: integration.usernameType,
              }}
              label={t(
                "usernameTypeLabel",
                "Username in clips requested by partner:",
              )}
              variant={theme.variants.select.default}
              labelStyles={labelStyles}
            />
            <SelectWithLabel
              applySpace={true}
              input={{
                id: `${integration.partnerId}.studioLoadoutType`,
                options: studioLoadoutTypeOptions,
                ...register(`${integration.partnerId}.studioLoadoutType`, {}),
                defaultValue: integration.studioLoadoutType,
              }}
              label={t("studioLoadoutTypeLabel", "Studio settings:")}
              variant={theme.variants.select.default}
              labelStyles={labelStyles}
            />
            {!lastIntegration && <DividerAlt></DividerAlt>}
          </Flex>
        );
      });
    } else {
      return (
        <Flex>
          <Body color="chalk" fontWeight={500} mt={2} mb={6}>
            {t(
              "noIntegrationsText",
              "You have no third parties integrated with your account.",
            )}
          </Body>
        </Flex>
      );
    }
  };

  return (
    <SettingsContainer
      title={t("title", "3rd Party Settings")}
      mb={6}
      subtitle={t(
        "subtitle",
        "Manage your settings for third-party integrations associated with your account.",
      )}
      subtitleTextColor="chalk"
    >
      <DividerAlt></DividerAlt>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        {renderIntegrations()}
        <Flex justifyContent="flex-start">
          <Button
            size="small"
            flex={true}
            mt={3}
            type="submit"
            disabled={!formState.isDirty}
          >
            {t("formSubmit", "Save")}
          </Button>
        </Flex>
      </StyledForm>
    </SettingsContainer>
  );
};

export { ThirdPartySettings };
